import React from "react";
import { Card, Row, Col, Form, Button } from "react-bootstrap";
import { useLanguage } from "../../context/LanguageContext.js";
import { t } from "../../utils/translations.js";

/**
 * Generic DateRangeFilter component
 *
 * Props:
 * - startDate (string): ISO date string for start date
 * - endDate (string): ISO date string for end date
 * - onStartDateChange (function): Called with new start date value
 * - onEndDateChange (function): Called with new end date value
 * - onClear (function): Called when clear button clicked
 * - sortOrder (string): Current sort order value ("newest" or "oldest")
 * - onSortChange (function): Called with new sort order value
 * - showSort (boolean): Whether to show sort dropdown (default: true)
 */
export default function DateRangeFilter({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onClear,
  sortOrder,
  onSortChange,
  showSort = true
}) {
  const { language } = useLanguage();
  const hasFilters = startDate || endDate;

  return (
    <Card
      className="mb-4 border-0 shadow-sm"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      <Card.Body className="p-4">
        <h6
          className="mb-4 text-uppercase fw-bold"
          style={{ color: "#495057", letterSpacing: "0.5px" }}
        >
          {t(language, "filtersSearch")}
        </h6>

        <Row className="g-3">
          <Col md={5}>
            <Form.Group>
              <Form.Label
                className="fw-semibold mb-2"
                style={{ fontSize: "0.95rem" }}
              >
                📅 {t(language, "startDate")}
              </Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={e => onStartDateChange(e.target.value)}
                className="border-1"
                style={{ borderColor: "#dee2e6" }}
              />
            </Form.Group>
          </Col>

          <Col md={5}>
            <Form.Group>
              <Form.Label
                className="fw-semibold mb-2"
                style={{ fontSize: "0.95rem" }}
              >
                📅 {t(language, "endDate")}
              </Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={e => onEndDateChange(e.target.value)}
                className="border-1"
                style={{ borderColor: "#dee2e6" }}
              />
            </Form.Group>
          </Col>

          <Col md={2} className="d-flex align-items-end">
            {hasFilters && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="w-100"
                onClick={onClear}
                style={{ borderColor: "#dee2e6" }}
              >
                ✕ {t(language, "clearFilter")}
              </Button>
            )}
          </Col>
        </Row>

        {showSort && (
          <Row className="g-3 mt-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label
                  className="fw-semibold mb-2"
                  style={{ fontSize: "0.95rem" }}
                >
                  🔀 {t(language, "sortBy")}
                </Form.Label>
                <Form.Select
                  value={sortOrder}
                  onChange={e => onSortChange(e.target.value)}
                  className="border-1"
                  style={{ borderColor: "#dee2e6", fontSize: "0.95rem" }}
                >
                  <option value="newest">
                    ⬇️ {t(language, "newestFirst")}
                  </option>
                  <option value="oldest">
                    ⬆️ {t(language, "oldestFirst")}
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}
